/**
 * TypeScript types for SIPS automation preferences
 * Used for syncing automation preferences between SIPS and AdvisorPortal
 */

/**
 * Bucket plan preference configuration
 */
export interface BucketPreference {
  bucketType: 'shortTerm' | 'mediumTerm' | 'longTerm' | 'discretionary';
  bucketName: string;
  startYear: number | null;
  endYear: number | null;
  returnPercentage: number;
}

/**
 * Minimum assets band configuration for banded minimum assets type
 */
export interface MinimumAssetsBand {
  bandSequence: number;
  minimumAssetValue: number;
  maximumAssetValue: number | null; // null = unlimited
  minimumPercent: number;
}

/**
 * SIPS automation preferences
 * These settings are stored in SIPS (configCustomer and configBucketPreferences tables)
 * and synced via the /api/sips/customer/{customerId}/automation-preferences endpoint
 */
export interface SipsAutomationPreferences {
  // Advisory fee percentage (0-100)
  advisoryFee: number | null;

  // Time horizon configuration (SC01)
  timeHorizonClientType: 'youngest' | 'oldest' | null;
  timeHorizonAge: number | null;

  // Savings strategy prior to distributions
  savingsStrategyPriorDistributions: 'automatic_makeup' | 'client_defined' | null;

  // Income decrease configuration
  ageToDecreaseIncome: number | null;
  percentToDecreaseIncome: number | null;
  whichClientDecreaseIncome: 'oldest' | 'youngest' | null;
  whenToApplyDecreaseIncome: 'all' | 'needed' | 'never' | null;

  // Inflation limit configuration (SC02)
  ageToLimitInflation: number | null;
  whenToApplyInflationLimit: 'all' | 'needed' | 'never' | null;

  // Earliest plan year before the decrease-income and inflation-limit
  // adjustments may fire (SC03). Blank/0 means no floor.
  minimumPlanYearBeforeAdjustments: number | null;

  // Social Security optimization
  socialSecurityOptimization: 'maximize_savings' | 'file_at_age' | null;
  socialSecurityFileAtAge: number | null; // 62-70

  // Scenario skip preferences
  // skipIncomeOptimization: skip the SC03 Income Optimization scenario entirely
  skipIncomeOptimization: boolean;
  // skipAssetAllocation: skip the SC04 Asset Allocation scenario (also skips
  // SC05/SC05.01 target-income movement); account returns are kept as entered
  skipAssetAllocation: boolean;

  // Roth conversion configuration
  skipRothConversion: boolean;
  maxConversionTaxBracket: number | null;

  // Survivorship analysis configuration (SC06.16)
  lifeExpectancyAgeForSurvivorshipAnalysis: number | null;
  whenToRunSurvivorshipAnalysis: 'never' | 'always' | 'when_needed' | null;
  // With 'when_needed': run only if a pension's survivorship % is <= this threshold
  pensionSurvivorshipThresholdPercent: number | null;

  // Minimum assets at end of plan as percentage of initial
  minimumEndOfPlanAssets: number | null;

  // Minimum assets type: fixed uses minimumEndOfPlanAssets, banded uses minimumAssetsBands
  minimumAssetsType: 'fixed' | 'banded' | null;

  // Minimum assets bands (used when minimumAssetsType is 'banded')
  minimumAssetsBands: MinimumAssetsBand[];

  // Method for calculating minimum assets floor
  // 'sum' adds percentage floor and legacy amount
  // 'max' uses the larger of the two
  minimumAssetsCalculationMethod: 'sum' | 'max' | null;

  // Bucket plan configuration
  bucketPreferences: BucketPreference[];

  // Read-only flag - indicates if automation is enabled in SIPS
  // This is used to show warning messages in the portal
  // Cannot be modified through the portal API
  allowPlanScoutAutomationFlag: boolean;
}

/**
 * API response wrapper for automation preferences
 */
export interface SipsAutomationPreferencesResponse {
  success: boolean;
  data?: SipsAutomationPreferences;
  error?: string;
  message?: string;
}

/**
 * Default values for bucket preferences
 */
export const DEFAULT_BUCKET_PREFERENCES: BucketPreference[] = [
  {
    bucketType: 'shortTerm',
    bucketName: 'Short Term',
    startYear: 1,
    endYear: 4,
    returnPercentage: 3.0
  },
  {
    bucketType: 'mediumTerm',
    bucketName: 'Medium Term',
    startYear: 5,
    endYear: 10,
    returnPercentage: 5.0
  },
  {
    bucketType: 'longTerm',
    bucketName: 'Long Term',
    startYear: 11,
    endYear: null,
    returnPercentage: 7.0
  },
  {
    bucketType: 'discretionary',
    bucketName: 'Discretionary',
    startYear: null,
    endYear: null,
    returnPercentage: 7.0
  }
];

/**
 * Default minimum assets bands (matches SIPS createDefaultBands)
 */
export const DEFAULT_MINIMUM_ASSETS_BANDS: MinimumAssetsBand[] = [
  { bandSequence: 1, minimumAssetValue: 0, maximumAssetValue: 600000, minimumPercent: 50 },
  { bandSequence: 2, minimumAssetValue: 600001, maximumAssetValue: 1000000, minimumPercent: 40 },
  { bandSequence: 3, minimumAssetValue: 1000001, maximumAssetValue: 1500000, minimumPercent: 35 },
  { bandSequence: 4, minimumAssetValue: 1500001, maximumAssetValue: 2000000, minimumPercent: 30 },
  { bandSequence: 5, minimumAssetValue: 2000001, maximumAssetValue: null, minimumPercent: 20 }
];

/**
 * Percent to decrease income options
 */
export const DECREASE_INCOME_PERCENT_OPTIONS = [
  { value: 5, label: '5%' },
  { value: 10, label: '10%' },
  { value: 15, label: '15%' },
  { value: 20, label: '20%' }
];

/**
 * Tax bracket options for Roth conversion
 */
export const TAX_BRACKET_OPTIONS = [
  { value: 10, label: '10%' },
  { value: 12, label: '12%' },
  { value: 22, label: '22%' },
  { value: 24, label: '24%' },
  { value: 32, label: '32%' },
  { value: 35, label: '35%' },
  { value: 37, label: '37%' }
];

/**
 * Per-plan preference overrides — a sparse subset of SipsAutomationPreferences
 * sent with a single create-case call. Absent fields inherit the advisor's SIPS
 * preferences; present values win in BOTH directions for that run only (e.g.
 * skipAssetAllocation: false force-runs asset allocation for an advisor whose
 * defaults skip it). Never persisted to the advisor's preferences.
 * advisoryFee and allowPlanScoutAutomationFlag are not overridable per-plan.
 */
export type PlanPreferenceOverrides = Partial<
  Omit<SipsAutomationPreferences, 'advisoryFee' | 'allowPlanScoutAutomationFlag'>
>;

/**
 * "Run a more comprehensive plan" preset: force-enable every technique the
 * advisor's defaults may skip, for this one run.
 */
export const COMPREHENSIVE_PLAN_OVERRIDES: PlanPreferenceOverrides = {
  skipIncomeOptimization: false,
  skipAssetAllocation: false,
  skipRothConversion: false
};
